<template>
  <div class="featured-ride-detail" :dir="isHebrew ? 'rtl' : 'ltr'">
    <title>{{ rideDataLocalized.title }} - TripPlanner</title>
    <SingleRideHeader :presentedText="rideDataLocalized.title" />
    <b-container>
      <div class="detail-content after_page_header">
        <div v-if="rideData.rideId" class="row">
          <!-- Image Section -->
          <div class="col-lg-6 mb-4">
            <div class="ride-detail-image-container">
              <img :src="rideDataLocalized.image" :alt="rideDataLocalized.title" class="ride-detail-image" />
              <div class="ride-price-badge-large">
                <span class="price-amount">{{ formatPrice(rideDataLocalized.price) }}</span>
                <span class="price-currency">{{ rideDataLocalized.currency || currencySymbol }}</span>
              </div>
            </div>
          </div>

          <!-- Content Section -->
          <div class="col-lg-6 mb-4">
            <div class="ride-detail-info">
              <h1 class="ride-detail-title">{{ rideDataLocalized.title }}</h1>
              <div class="ride-id-badge">
                <span>{{ guideInfoLocalized.featuredRidesDetailTripNumberLabel || t('featuredRideDetailTripNumberLabel') }} {{ rideData.rideId }}</span>
              </div>
              <div class="ride-description">
                <h3>{{ guideInfoLocalized.featuredRidesDetailDescriptionTitle || t('featuredRideDetailDescriptionTitle') }}</h3>
                <p v-html="formatTextWithNewlines(rideDataLocalized.shortText)"></p>
                <p v-if="rideDataLocalized.fullDescription" class="full-description" v-html="formatTextWithNewlines(rideDataLocalized.fullDescription)"></p>
              </div>

              <!-- Technical Requirements Section -->
              <div class="technical-requirements-section">
                <h3>{{ guideInfoLocalized.technicalRequirementsTitle || guideInfo.technicalRequirementsTitle }}</h3>
                <ul class="requirements-list">
                  <li v-for="(item, index) in (guideInfoLocalized.technicalRequirementsList || guideInfo.technicalRequirementsList)" :key="index">
                    <span class="requirement-checkmark">✓</span>
                    <span class="requirement-text">{{ item }}</span>
                  </li>
                </ul>
                <p class="technical-note">
                  {{ guideInfoLocalized.technicalNote || guideInfo.technicalNote }}
                </p>
              </div>

              <div class="ride-actions mt-4">
                <b-button 
                  variant="primary" 
                  size="lg" 
                  class="action-btn"
                  @click="goToPurchase"
                >
                  {{ guideInfoLocalized.featuredRidesDetailBuyButtonText || t('featuredRideDetailPurchaseTrip') }}
                </b-button>
                <b-button 
                  variant="outline-primary" 
                  size="lg" 
                  class="action-btn"
                  @click="goBack"
                >
                  {{ guideInfoLocalized.featuredRidesDetailBackButtonText || t('featuredRideDetailBackToList') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="not-found">
          <h2>{{ guideInfoLocalized.featuredRidesNotFoundTitle || t('featuredRideDetailNotFoundTitle') }}</h2>
          <p>{{ guideInfoLocalized.featuredRidesNotFoundDescription || t('featuredRideDetailNotFoundBody') }}</p>
          <b-button variant="primary" @click="goBack">
            {{ guideInfoLocalized.featuredRidesNotFoundBackButtonText || t('featuredRideDetailBackShort') }}
          </b-button>
        </div>
      </div>
    </b-container>
    <PageFooter
      v-if="!isSubdomain"
    />
  </div>
</template>

<script>
import PageFooter from "@/components/PageFooter.vue";
import SingleRideHeader from "@/components/SingleRideHeader";
import config from "@/common/config";
import { mapGetters } from 'vuex';
import { translationMixin } from "@/mixins/translationMixin";

export default {
  name: "FeaturedRideDetail",
  mixins: [translationMixin],
  components: {
    PageFooter,
    SingleRideHeader
  },
  data() {
    return {
      rideData: {}
    };
  },
  computed: {
    ...mapGetters("theme", ["isSubdomain"]),
    currencySymbol () {
      return this.$store.state.regionConfig.currencySymbol
    },
    rideDataLocalized() {
      return this.getLocalizedRide(this.rideData);
    },
    guideInfoLocalized() {
      return this.getLocalizedGuideInfo(this.guideInfo);
    },
    guideInfo() {
      // Find which manager owns this ride
      const featuredPages = config.config.FEATURED_RIDES || [];
      const rideId = this.rideData.rideId;
      
      if (!rideId) {
        // If no ride data, use default
        const guideInfoConfig = config.config.DIGITAL_GUIDE_INFO || {};
        return guideInfoConfig.default || {};
      }
      
      // Find the page that owns this ride
      const owningPage = featuredPages.find(page => {
        if (!Array.isArray(page.rides)) {
          return false;
        }
        return page.rides.some(ride => 
          ride.rideId === parseInt(rideId)
        );
      });
      
      // Get manager URL name from the owning page
      const managerUrlName = owningPage ? owningPage.managerUrlName : null;
      
      // Get guide info for this manager, or use default
      const guideInfoConfig = config.config.DIGITAL_GUIDE_INFO || {};
      const managerInfo = managerUrlName && guideInfoConfig[managerUrlName] 
        ? guideInfoConfig[managerUrlName] 
        : null;
      
      // Merge manager-specific info with default (manager overrides default)
      const defaultInfo = guideInfoConfig.default || {};
      return managerInfo ? { ...defaultInfo, ...managerInfo } : defaultInfo;
    }
  },
  mounted() {
    // Get ride ID from route params
    const rideId = this.$route.params.id;
    this.loadRideData(rideId);
  },
  watch: {
    '$route'(to) {
      // Reload data when route changes
      this.loadRideData(to.params.id);
    }
  },
  methods: {
    getLocalizedRide(ride) {
      if (!ride || typeof ride !== 'object') {
        return {};
      }
      const lang = this.getCurrentLanguage || 'he';
      const localized = ride.localized && ride.localized[lang] ? ride.localized[lang] : {};
      return { ...ride, ...localized };
    },
    getLocalizedGuideInfo(guideInfo) {
      if (!guideInfo || typeof guideInfo !== 'object') {
        return {};
      }
      const lang = this.getCurrentLanguage || 'he';
      const localized = guideInfo.localized && guideInfo.localized[lang] ? guideInfo.localized[lang] : {};
      return { ...guideInfo, ...localized };
    },
    getAllFeaturedRides() {
      const pages = config.config.FEATURED_RIDES || [];
      if (!Array.isArray(pages)) {
        return [];
      }
      return pages.flatMap(page => {
        if (Array.isArray(page.rides)) {
          return page.rides;
        }
        return page ? [page] : [];
      });
    },
    loadRideData(rideId) {
      // Find ride in config by rideId
      const rides = this.getAllFeaturedRides();
      this.rideData = rides.find(ride => 
        ride.rideId === parseInt(rideId)
      ) || {};
      
      // Set language based on owning page or ride override
      const owningPage = this.getOwningManager();
      const rideLanguage = this.rideData?.language || owningPage?.language || null;
      if (rideLanguage) {
        this.$store.dispatch('language/setLanguageFromRide', rideLanguage);
      }
    },
    formatPrice(price) {
      const locale = this.getCurrentLanguage === 'en' ? 'en-US' : 'he-IL';
      return price ? price.toLocaleString(locale) : '0';
    },
    formatTextWithNewlines(text) {
      if (!text) return '';
      return text.replace(/\n/g, '<br>');
    },
    goToPurchase() {
      // Navigate directly to payment page with ride information
      this.$router.push({
        name: 'payment',
        query: {
          rideId: this.rideData.rideId
        }
      });
    },
    getOwningManager() {
      // Find which manager owns this ride
      const featuredPages = config.config.FEATURED_RIDES || [];
      const rideId = this.rideData.rideId;
      
      if (!rideId) {
        return null;
      }
      
      // Find the page that owns this ride
      return featuredPages.find(page => {
        if (!Array.isArray(page.rides)) {
          return false;
        }
        return page.rides.some(ride => 
          ride.rideId === parseInt(rideId)
        );
      });
    },
    goBack() {
      const owningManager = this.getOwningManager();
      
      if (owningManager) {
        // Navigate to the manager's featured rides page
        if (owningManager.slug) {
          this.$router.push({
            name: 'featured-rides-landing-manager',
            params: { managerUrlName: owningManager.slug }
          });
        } else if (owningManager.managerUrlName) {
          this.$router.push({
            name: 'featured-rides-landing-manager',
            params: { managerUrlName: owningManager.managerUrlName }
          });
        } else {
          // Fallback to default
          this.$router.push({ name: 'featured-rides-landing' });
        }
      } else {
        // No manager found, go to default
        this.$router.push({ name: 'featured-rides-landing' });
      }
    }
  },
  metaInfo() {
    return {
      title: this.rideDataLocalized?.title ? `${this.rideDataLocalized.title} - TripPlanner` : this.t('featuredRideDetailMetaTitle'),
      meta: [
        { property: 'og:title', content: this.rideDataLocalized?.title || this.t('featuredRideDetailMetaTitleShort') },
        { property: 'og:description', content: this.rideDataLocalized?.shortText || this.t('featuredRideDetailMetaDescription') },
        { property: 'og:type', content: 'website' },
        { property: 'og:image', content: this.rideDataLocalized?.image || 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/page_images%2Fphone_display_app.png?alt=media&token=ccfc9989-224e-40be-ae57-aa6ed4db8637' },
        { property: 'og:url', content: `https://tripplanner.co.il${this.$route.fullPath}` },
        { name: 'description', content: this.rideDataLocalized?.shortText || this.t('featuredRideDetailMetaDescription') }
      ],
      link: [
        { rel: 'canonical', href: `https://tripplanner.co.il${this.$route.fullPath}` }
      ]
    };
  }
};
</script>

<style scoped>
[dir="ltr"] .ride-detail-info {
  text-align: left;
}

[dir="ltr"] .ride-detail-title {
  direction: ltr;
}

.detail-content {
  min-height: 60vh;
  padding: 3rem 0;
}

.ride-detail-image-container {
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.ride-detail-image {
  width: 100%;
  height: auto;
  display: block;
}

.ride-price-badge-large {
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.ride-price-badge-large .price-amount {
  font-size: 2.2rem;
  font-weight: bold;
}

.ride-price-badge-large .price-currency {
  font-size: 1.6rem;
}

.ride-detail-info {
  text-align: right;
}

.ride-detail-title {
  font-size: 3rem;
  color: #0d2f29;
  margin-bottom: 1.5rem;
  font-weight: bold;
}

.ride-id-badge {
  display: inline-block;
  background: #f8f9fa;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 1.4rem;
  color: #666;
  margin-bottom: 2rem;
}

.ride-description {
  margin-bottom: 2rem;
}

.ride-description h3 {
  font-size: 2rem;
  color: #0d2f29;
  margin-bottom: 1rem;
}

.ride-description p {
  font-size: 1.6rem;
  color: #666;
  line-height: 1.8;
  margin-bottom: 1rem;
}

.full-description {
  white-space: pre-line;
}

.technical-requirements-section {
  margin: 3rem 0;
  padding: 2.4rem;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.technical-requirements-section h3 {
  font-size: 2.4rem;
  color: #0d2f29;
  margin-bottom: 2rem;
  font-weight: 700;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
}

.requirements-list li {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 1.2rem;
  color: #495057;
}

.requirement-checkmark {
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: bold;
}

.requirement-text {
  font-size: 1.8rem;
  line-height: 1.8;
}

.technical-note {
  font-size: 1.6rem;
  line-height: 1.8;
  color: #6c757d;
  font-style: italic;
  padding: 1.6rem;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 12px;
  border-right: 4px solid #ffc107;
  margin-top: 2rem;
  margin-bottom: 0;
}

.ride-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.action-btn {
  padding: 1rem 2rem;
  font-size: 1.6rem;
  border-radius: 8px;
  min-width: 150px;
}

.action-btn:first-child {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  border: none;
}

.action-btn:first-child:hover {
  background: linear-gradient(45deg, #07A0C3, #34D0B6);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(7, 160, 195, 0.3);
}

.not-found {
  text-align: center;
  padding: 4rem 2rem;
}

.not-found h2 {
  font-size: 2.5rem;
  color: #0d2f29;
  margin-bottom: 1rem;
}

.not-found p {
  font-size: 1.6rem;
  color: #666;
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .detail-content {
    padding: 2rem 1rem 3rem;
  }

  .ride-detail-title {
    font-size: 2.2rem;
  }

  .ride-description h3 {
    font-size: 1.6rem;
  }

  .ride-description p {
    font-size: 1.4rem;
  }

  .technical-requirements-section {
    padding: 1.6rem;
    margin: 2rem 0;
  }

  .technical-requirements-section h3 {
    font-size: 2rem;
  }

  .requirement-text {
    font-size: 1.6rem;
  }

  .technical-note {
    font-size: 1.4rem;
    padding: 1.2rem;
  }

  .ride-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }
}
</style>


